import { useState } from "react";
import { IonModal, IonHeader, IonToolbar, IonTitle, IonContent, IonButtons } from "@ionic/react";
import { X, Search } from "lucide-react";
import { useStore } from "../store/useStore";
import type { ITask } from "../types";

interface TaskPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function TaskPickerModal({ isOpen, onClose }: TaskPickerModalProps) {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const tasks = useStore((state) => state.tasks);
  const moveToToday = useStore((state) => state.moveToToday);

  const backlogTasks = tasks
    .filter((t) => t.status === "backlog")
    .filter((t) => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      const title = (t.title || t.raw_input).toLowerCase();
      return title.includes(q);
    });

  const toggleSelect = (uuid: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) {
        next.delete(uuid);
      } else {
        next.add(uuid);
      }
      return next;
    });
  };

  const handleAdd = async () => {
    const promises = Array.from(selected).map((uuid) => moveToToday(uuid));
    await Promise.all(promises);
    setSelected(new Set());
    setSearch("");
    onClose();
  };

  const renderTask = (task: ITask) => {
    const isSelected = selected.has(task.uuid);
    return (
      <button
        key={task.uuid}
        onClick={() => toggleSelect(task.uuid)}
        className={`w-full text-left p-3 rounded-lg border transition-colors ${
          isSelected
            ? "border-primary bg-primary/5"
            : "border-border bg-surface hover:bg-surface-raised"
        }`}
      >
        <div className="flex items-center gap-3">
          <div
            className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
              isSelected
                ? "border-primary bg-primary"
                : "border-muted-foreground/40"
            }`}
          >
            {isSelected && (
              <svg className="w-3 h-3 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">
              {task.title || task.raw_input}
            </p>
            {task.urgency && (
              <span className="text-xs text-muted-foreground capitalize">
                {task.urgency}
              </span>
            )}
          </div>
        </div>
      </button>
    );
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose}>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <button onClick={onClose} className="p-2 text-muted-foreground">
              <X className="w-5 h-5" />
            </button>
          </IonButtons>
          <IonTitle>Pick Tasks for Today</IonTitle>
          <IonButtons slot="end">
            {selected.size > 0 && (
              <button onClick={handleAdd} className="p-2 text-primary font-medium text-sm">
                Add {selected.size}
              </button>
            )}
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className="p-4 space-y-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search backlog..."
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-input bg-background text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>

          {/* Task list */}
          {backlogTasks.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground text-sm">
                {search ? "No matching tasks" : "Backlog is empty"}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {backlogTasks.map(renderTask)}
            </div>
          )}
        </div>
      </IonContent>
    </IonModal>
  );
}
